/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

const localYmdToUtcStartIso = ymd => {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

const localYmdToUtcEndIso = ymd => {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1, id) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
        id,
      },
    });
  }

  getCallHistory({ page = 1, perPage = 25, filters = {} } = {}) {
    const filterParams = {};
    const setIfPresent = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length === 0) return;
        filterParams[key] = value.join(',');
        return;
      }
      if (typeof value === 'string' && value.trim() === '') return;
      filterParams[key] = value;
    };
    setIfPresent('caller_ids', filters.callerIds);
    setIfPresent('statuses', filters.statuses);
    setIfPresent('contact_query', filters.contactQuery);
    setIfPresent('date_from', localYmdToUtcStartIso(filters.dateFrom));
    setIfPresent('date_to', localYmdToUtcEndIso(filters.dateTo));
    setIfPresent('duration_min', filters.durationMin);
    setIfPresent('duration_max', filters.durationMax);
    setIfPresent('score_min', filters.scoreMin);
    setIfPresent('score_max', filters.scoreMax);

    return axios.get(`${this.url}/call_history`, {
      params: { page, per_page: perPage, ...filterParams },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV(range = {}) {
    const params = {
      timezone_offset: getTimeOffset(),
      ...(range.since != null &&
        range.until != null && {
          since: range.since,
          until: range.until,
        }),
      ...(range.type && { type: range.type }),
      ...(range.id != null && { id: range.id }),
    };

    return axios.get(`${this.url}/conversation_traffic`, { params });
  }

  getContactTrafficCSV(range = {}) {
    const params = {
      timezone_offset: getTimeOffset(),
      ...(range.since != null &&
        range.until != null && {
          since: range.since,
          until: range.until,
        }),
      ...(range.type && { type: range.type }),
      ...(range.id != null && { id: range.id }),
    };

    return axios.get(`${this.url}/contact_traffic`, { params });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  conversationAttendanceBaseUrl() {
    return this.url.replace(/\/reports$/, '');
  }

  getConversationAttendanceReport({ since, until }) {
    return axios.get(`${this.conversationAttendanceBaseUrl()}/conversation_attendance`, {
      params: {
        since,
        until,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  exportConversationAttendanceReport({ since, until }) {
    return axios
      .get(`${this.conversationAttendanceBaseUrl()}/conversation_attendance/export`, {
        params: {
          since,
          until,
          timezone_offset: getTimeOffset(),
        },
        responseType: 'text',
      })
      .then(response => response.data);
  }
}

export default new ReportsAPI();
